import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Label,
  Input,
  Col,
  Button,
} from "reactstrap";
import Select from "react-select";
import CustomerServices from "../../services/OrderingServices/CustomerServices";
import MobileCustomerForm from "../../OrderingSystem/Customer/Components/MobileCustomerForm";
import toast from "react-hot-toast";

const CreditModal = ({
  isOpen,
  toggle,
  setCreditData,
  initialCustomer,
  creditData,
  resetCredit,
  calculateTotals,
  maxCreditAllowed,
}) => {
  const [loading, setLoading] = useState(false);
  const [customerList, setCustomerList] = useState([]);
  const [addModal, setAddModal] = useState(false);

  // ----------------------------------------------------------------
  // FORM
  // ----------------------------------------------------------------
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      amount: creditData?.amount || 0,
      remarks: creditData?.remarks || "",
      customerId: creditData?.customerId || initialCustomer?.id || null,
    },
    validationSchema: Yup.object({
      amount: Yup.number()
        .typeError("Amount must be a number")
        .positive("Amount must be positive")
        .max(
          maxCreditAllowed || 0,
          `Amount cannot exceed Rs ${(maxCreditAllowed || 0).toFixed(2)}`
        ),
      customerId: Yup.number()
        .min(1, "Please select a customer")
        .required("Customer is required"),
    }),
    onSubmit: (values) => {
      // Double-check validation
      if (values.amount > (maxCreditAllowed || 0)) {
        toast.error(
          `Credit amount cannot exceed Rs ${(maxCreditAllowed || 0).toFixed(2)}`
        );
        return;
      }
      setCreditData(values);
      handleCloseOnly();
    },
  });

  // ----------------------------------------------------------------
  // FETCH CUSTOMERS
  // ----------------------------------------------------------------
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await CustomerServices.getCustomerList();
      setCustomerList(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // ----------------------------------------------------------------
  // SELECT OPTIONS
  // ----------------------------------------------------------------
  const customerOptions = customerList.map((c) => ({
    value: c.id,
    label: `${c.firstName} ${c.lastName || ""}`,
  }));

  const selectedCustomer = customerOptions.find(
    (opt) => opt.value === formik.values.customerId
  );

  const handleCustomerChange = (option) => {
    formik.setFieldValue("customerId", option ? option.value : null);
  };

  const handleCloseOnly = () => toggle(false);

  const handleResetAndClose = () => {
    formik.resetForm();
    resetCredit();
    toggle(false);
  };

  const handleCustomerAdded = (newCustomer) => {
    setCustomerList((prev) => [...prev, newCustomer]);
    formik.setFieldValue("customerId", newCustomer.id);
  };

  return (
    <>
      <Offcanvas
        isOpen={isOpen}
        toggle={handleResetAndClose}
        direction="end"
        className="offcanvas-end"
      >
        <OffcanvasHeader toggle={handleResetAndClose}>
          Add Credit
        </OffcanvasHeader>

        <form onSubmit={formik.handleSubmit}>
          <OffcanvasBody>
            {/* Amount */}
            <Col className="mb-3">
              <Label>
                Amount{" "}
                <span className="text-muted small">
                  (Max: Rs {(maxCreditAllowed || 0).toFixed(2)})
                </span>
              </Label>
              <Input
                type="number"
                name="amount"
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched.amount && !!formik.errors.amount}
                placeholder="Enter credit amount"
                step="0.01"
                className={
                  formik.values.amount > (maxCreditAllowed || 0)
                    ? "border-danger"
                    : ""
                }
              />
              {formik.touched.amount && formik.errors.amount && (
                <div className="text-danger small mt-1">
                  {formik.errors.amount}
                </div>
              )}
            </Col>

            {/* Customer */}
            <Col className="mb-3">
              <Label>
                Customer <span className="text-danger">*</span>
              </Label>
              <div className="d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <Select
                    options={customerOptions}
                    value={selectedCustomer || null}
                    onChange={handleCustomerChange}
                    placeholder="Select customer"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                  {formik.touched.customerId && formik.errors.customerId && (
                    <div className="text-danger mt-1">
                      {formik.errors.customerId}
                    </div>
                  )}
                </div>
                <div>
                  <i
                    className="ri-user-add-line fs-4"
                    style={{ cursor: "pointer" }}
                    onClick={() => setAddModal(true)}
                  ></i>
                </div>
              </div>
            </Col>

            {/* Remarks */}
            <Col className="mb-3">
              <Label>
                Remarks <span className="text">(Optional)</span>
              </Label>
              <Input
                type="textarea"
                rows={5}
                name="remarks"
                value={formik.values.remarks}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched.remarks && !!formik.errors.remarks}
              />
              {formik.touched.remarks && formik.errors.remarks && (
                <div className="text-danger">{formik.errors.remarks}</div>
              )}
            </Col>

            {/* Actions */}
            <div className="d-flex justify-content-end gap-2">
              <Button
                color="danger"
                type="button"
                onClick={handleResetAndClose}
              >
                Cancel
              </Button>
              <Button
                color="success"
                type="submit"
                disabled={
                  !formik.isValid ||
                  formik.values.amount > (maxCreditAllowed || 0)
                }
              >
                Add Credit
              </Button>
            </div>
          </OffcanvasBody>
        </form>
      </Offcanvas>

      {/* Second Offcanvas */}
      <MobileCustomerForm
        modal={addModal}
        toggleModal={() => setAddModal(false)}
        setCustomerList={setCustomerList}
        customerList={customerList}
        selectedCustomer={selectedCustomer}
        fetchCustomers={fetchCustomers}
        onCustomerAdded={handleCustomerAdded}
      />
    </>
  );
};

export default CreditModal;
